// Runtime accent theming. The studio ships two token systems that both need
// recolouring when the accent changes: the ported prototype vars (--primary,
// --primary-2, --primary-soft, --primary-ink) and Tailwind v4's @theme tokens
// (--color-primary, …). apply_accent sets all eight on :root.
//
// The tenant's accent is configurable in Settings > Brand. Default is forest
// green (matches the prototype demo). The active accent is cached in
// localStorage so boot applies it instantly — no maroon→forest flash.

export interface AccentPalette {
  primary: string;
  primary2: string;
  soft: string;
  ink: string;
}

// The four brand accents (hex keys, lowercased) — same set as the Settings
// swatches and the prototype's ACCENTS map.
export const ACCENTS: Record<string, AccentPalette> = {
  '#7b1f2c': { primary: '#7B1F2C', primary2: '#9A2A3A', soft: '#F2E2E0', ink: '#4A0E16' }, // maroon
  '#2d3a7b': { primary: '#2D3A7B', primary2: '#3F4FA0', soft: '#E1E4F2', ink: '#0F164A' }, // indigo
  '#2e5f3d': { primary: '#2E5F3D', primary2: '#3F7A52', soft: '#DCEAD9', ink: '#143521' }, // forest
  '#26201a': { primary: '#26201A', primary2: '#3D342A', soft: '#E6DFD3', ink: '#0E0B08' }, // charcoal
};

export const DEFAULT_ACCENT = '#2E5F3D'; // forest
const LS_KEY = 'chitos.accent';

/** Unknown hex (operator typed a custom colour): use it as primary and fall
 *  back to neutral soft/ink so the UI stays legible. */
function derive(hex: string): AccentPalette {
  return { primary: hex, primary2: hex, soft: 'rgba(0,0,0,0.06)', ink: hex };
}

/** Apply an accent (by primary hex) to the live document + cache it. */
export function apply_accent(hex: string): void {
  if (typeof document === 'undefined') return;
  const pal = ACCENTS[hex.toLowerCase()] ?? derive(hex);
  const s = document.documentElement.style;
  s.setProperty('--primary', pal.primary);
  s.setProperty('--primary-2', pal.primary2);
  s.setProperty('--primary-soft', pal.soft);
  s.setProperty('--primary-ink', pal.ink);
  s.setProperty('--color-primary', pal.primary);
  s.setProperty('--color-primary-2', pal.primary2);
  s.setProperty('--color-primary-soft', pal.soft);
  s.setProperty('--color-primary-ink', pal.ink);
  try {
    window.localStorage.setItem(LS_KEY, pal.primary);
  } catch {
    // localStorage unavailable (private mode) — theming still applied in-memory.
  }
}

/** The active accent hex: the cached choice, else the forest default. */
export function current_accent(): string {
  try {
    return window.localStorage.getItem(LS_KEY) || DEFAULT_ACCENT;
  } catch {
    return DEFAULT_ACCENT;
  }
}

/** Apply the cached/default accent. Call once before first render. */
export function boot_accent(): void {
  apply_accent(current_accent());
}
